/**
 * THE DOCUMENT SHELL, FOR EVERY RENDER OF THE DOCUMENT (2026-09-15).
 *
 * `renderDocument(blocks, shell)` wraps the rows in the SFMC blank template
 * (`ECH_Blank_Template`, services/sfmcBlocks.ts findShell) when one is in hand. Until
 * today only StudioView held that shell — `App.tsx` rendered `renderDocument(blocks)`
 * with NO shell, and App's render is `builtHtml`: the form preview AND the HTML the
 * submit attaches. So the shell hook was dead twice over: the block that matched it
 * was never in the pull (a `template` asset), and had it been, the submit path would
 * still have shipped the fallback head. Measured live on dev after the block landed
 * in SFMC: snapshot carried `ECH_Blank_Template`, the form preview's head was still
 * the 4 KB fallback.
 *
 * One source, read the same way everywhere: the adopted snapshot synchronously
 * (warm browser, no flash of the fallback head), then the session's single pull, then
 * the watch — the same three moments StudioView already reacts to. App and StudioView
 * both call this, so the preview, the submit, the favorites copy and the Studio
 * export are one document.
 */
import { useEffect, useMemo, useState } from "react";
import { type SfmcBlock } from "./sfmcBlocks";
import { sfmcBlocksOnce, sfmcBlocksWatch } from "./sfmcSource";
import { echActiveTemplate, echAdoptSnapshot, echAdoptedBlocks, echShellFrom } from "./echSnap";

/** re-render on the shell the adopted payload resolved to — the same string
 *  `renderDocument` picks up by default, so callers that pass it explicitly and
 *  callers that don't render one document.
 *
 *  PER TEMPLATE (2026-10-06): `tpl` names the template being rendered; a template
 *  that declares `"shell"` in its @GUARDRAILS gets that shell (echSnap echShellFrom).
 *  Omitted = the request's template App registered (`echSetActiveTemplate`). */
export function useEchShell(tpl?: string | null): string | null {
  const [blocks, setBlocks] = useState<SfmcBlock[] | null>(() => { echAdoptSnapshot(); return echAdoptedBlocks(); });
  useEffect(() => {
    let live = true;
    /* READ, NEVER ADOPT. StudioView's own pull/watch handlers adopt the payload and
       key their canvas rebuild on that adoption being the first — adopting here won
       that race in the poll test and froze the canvas on stale markup. So this hook
       resolves the shell from the payload directly and leaves adoption alone. */
    const take = (b: SfmcBlock[]) => { if (!live) return; if (b && b.length) setBlocks(b); };
    sfmcBlocksOnce().then(take).catch(() => { /* a failed refresh keeps whatever is in hand — echSnap rule 2 */ });
    const unsub = sfmcBlocksWatch(take);
    return () => { live = false; unsub(); };
  }, []);
  const name = tpl === undefined || tpl === null ? echActiveTemplate() : tpl;
  return useMemo(() => echShellFrom(blocks, name), [blocks, name]);
}
